// Catalog.ID Client — what a print step's money leg runs on
// ============================================================================
// apps/bitcash/docs/plans/bitcash-v2.md M12. Before a wizard calls `setUpWallet` it needs:
//
//   * the ledger the node names — none, and there is no money leg at all
//     (`ledgerContext()`; there is no built-in ledger);
//   * the wallet's REF on that ledger's network (`wallet/exists` asks by ref);
//   * the receive address and token codes the leg will create — generated
//     here ONCE, kept in the onboarding stash under the wizard's keys, and
//     written ahead into the account's instruments blob, all before any ledger
//     call: the ledger never returns a code (T2), so a resume must re-send the
//     same ones, which the ledger answers idempotently. The token is the
//     member's default token, minted UNBOUND (no payee) and scoped to the node's
//     service types (apps/docs/plans/second-node-and-bitcash-open-issues.md
//     §9.3), so it is written down only when the node names those terms.
//
// The caller persists the stash (`stashSync`) right after this returns. A
// failed blob write is tolerated — the stash still holds the codes and a print
// is never blocked by bookkeeping.

import { newCode, walletRefOf, TYPE_AUTH, TYPE_FUND } from '../crypto/bitcash';
import { ledgerContext, type LedgerContext } from '../state/bitcash-context';
import { recordFund, recordToken } from '../state/bitcash-instruments-store';
import { DEFAULT_TOKEN_LABEL } from './bits-release';
import { fetchRegistrationConfig, type DefaultTokenTerms } from './payment-flow';

export interface WalletSetupPlan {
    walletRef: string;
    /** The ledger's network the ref is computed on. */
    networkId: LedgerContext['networkId'];
    instruments: { fund: string; token: string };
}

export interface WalletSetupPlanDeps {
    ledger(): Promise<LedgerContext | null>;
    /** The default token's terms (`GET /api/registration/config` → `defaultToken`), or null. */
    defaultToken(): Promise<DefaultTokenTerms | null>;
    recordFund(walletRef: string, code: string): Promise<unknown>;
    recordToken(walletRef: string, token: { code: string; purpose: 'default'; label: string; payee: string | null }): Promise<unknown>;
}

const defaultDeps: WalletSetupPlanDeps = {
    ledger: ledgerContext,
    defaultToken: async () => (await fetchRegistrationConfig()).defaultToken,
    recordFund: (walletRef, code) => recordFund(walletRef, code),
    recordToken: (walletRef, token) => recordToken(walletRef, token),
};

export async function planWalletSetup(
    args: {
        walletKey: string;
        /** The wizard's shared stash; the codes are stored under `stashKeys`. */
        stash: Record<string, unknown>;
        stashKeys: { fund: string; token: string };
    },
    deps: WalletSetupPlanDeps = defaultDeps,
): Promise<WalletSetupPlan | null> {
    const ledger = await deps.ledger();
    if (ledger === null) return null;

    const walletRef = walletRefOf(args.walletKey, ledger.networkId);
    const fund = typeof args.stash[args.stashKeys.fund] === 'string'
        ? args.stash[args.stashKeys.fund] as string : newCode(TYPE_FUND);
    const token = typeof args.stash[args.stashKeys.token] === 'string'
        ? args.stash[args.stashKeys.token] as string : newCode(TYPE_AUTH);
    args.stash[args.stashKeys.fund] = fund;
    args.stash[args.stashKeys.token] = token;

    try {
        await deps.recordFund(walletRef, fund);
        const terms = await deps.defaultToken();
        if (terms !== null && terms.serviceTypes.length > 0) {
            await deps.recordToken(walletRef, { code: token, purpose: 'default', label: DEFAULT_TOKEN_LABEL, payee: null });
        }
    } catch (err) {
        console.warn('[bitcash] the receive address and token codes were kept in the onboarding stash only:', err);
    }
    return { walletRef, networkId: ledger.networkId, instruments: { fund, token } };
}
